import React, { useRef, useState } from 'react';
import S from '../GestionDonnees.module.scss';

// Parse CSV: primeira linha = cabeçalhos, separador virgula ou ponto-virgula
const parseCsv = (texto) => {
    const linhas = texto.trim().split(/\r?\n/);
    if (linhas.length < 2) return [];
    const sep = linhas[0].includes(';') ? ';' : ',';
    const cabecalhos = linhas[0].split(sep).map((h) => h.trim());
    return linhas.slice(1).map((linha) => {
        const valores = linha.split(sep).map((v) => v.trim());
        const obj = {};
        cabecalhos.forEach((h, i) => { obj[h] = valores[i] ?? ''; });
        return obj;
    });
};

// Parse JSON: aceita array directo ou objecto com propriedade array
const parseJson = (texto) => {
    const parsed = JSON.parse(texto);
    if (Array.isArray(parsed)) return parsed;
    const chave = Object.keys(parsed).find((k) => Array.isArray(parsed[k]));
    return chave ? parsed[chave] : [];
};

const COLONNES_ATTENDUES = ['timestamp', 'capteur', 'valeur', 'unite'];

const ImportTab = () => {
    const [fichier, setFichier] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [apercu, setApercu] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [importOk, setImportOk] = useState(false);
    const inputRef = useRef(null);

    const traiterFichier = (file) => {
        if (!file) return;
        setFichier(file);
        setApercu(null);
        setErreur(null);
        setImportOk(false);

        const ext = file.name.split('.').pop().toLowerCase();
        if (!['csv', 'json'].includes(ext)) {
            setErreur('Format non supporté. Utilisez CSV ou JSON.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const texte = e.target.result;
                const lignes = ext === 'csv' ? parseCsv(texte) : parseJson(texte);
                if (lignes.length === 0) {
                    setErreur('Fichier vide ou format invalide.');
                    return;
                }
                setApercu(lignes);
            } catch {
                setErreur('Impossible de lire le fichier. Vérifiez le format.');
            }
        };
        reader.readAsText(file, 'UTF-8');
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        traiterFichier(e.dataTransfer.files[0]);
    };

    const handleFileChange = (e) => {
        traiterFichier(e.target.files[0]);
    };

    const handleImporter = () => {
        // Point d'extension : ici on appellerait l'API avec "apercu"
        setImportOk(true);
    };

    // Colonnes à afficher : celles du fichier ou les attendues
    const colonnes = apercu && apercu.length > 0 ? Object.keys(apercu[0]) : COLONNES_ATTENDUES;

    return (
        <>
            <div className={S.importSection}>
                <div
                    className={[S.dropZone, dragOver ? S.dropZoneActive : ''].join(' ')}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => inputRef.current?.click()}
                >
                    <div className={S.dropIcon}>📂</div>
                    <div className={S.dropLabel}>Glisser-déposer un fichier ici</div>
                    <p>ou cliquer pour sélectionner</p>
                    <p style={{ marginTop: 4, fontSize: 12 }}>
                        Formats acceptés : <strong>CSV</strong>, <strong>JSON</strong>
                    </p>
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".csv,.json"
                        className={S.fileInput}
                        onChange={handleFileChange}
                    />
                </div>

                {fichier && !erreur && (
                    <div style={{ marginTop: 12, fontSize: 13, color: '#555' }}>
                        📄 <strong>{fichier.name}</strong> ({(fichier.size / 1024).toFixed(1)} Ko)
                    </div>
                )}

                {erreur && (
                    <div className={S.alertErreur}>{erreur}</div>
                )}

                <div className={S.formatInfo}>
                    <strong>Format CSV attendu :</strong>{' '}
                    <code>timestamp, capteur, valeur, unite</code>
                    <br />
                    <strong>Format JSON attendu :</strong>{' '}
                    <code>{`[{ "timestamp": "...", "capteur": "...", "valeur": 0.0, "unite": "°C" }]`}</code>
                </div>

                {importOk && (
                    <div className={S.alertSucces}>
                        ✅ {apercu?.length ?? 0} ligne(s) importée(s) avec succès.
                    </div>
                )}

                <button
                    className={S.btnImport}
                    disabled={!apercu || importOk}
                    onClick={handleImporter}
                >
                    Importer les données
                </button>
            </div>

            {apercu && (
                <div className={S.visualSection}>
                    <div className={S.sectionHeader}>
                        <h3>Prévisualisation — {apercu.length} ligne(s)</h3>
                        <span style={{ fontSize: 12, color: '#888' }}>{fichier?.name}</span>
                    </div>
                    <div className={S.tableWrapper}>
                        <table className={S.dataTable}>
                            <thead>
                                <tr>
                                    {colonnes.map((col) => (
                                        <th key={col}>{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {apercu.slice(0, 50).map((row, i) => (
                                    <tr key={i}>
                                        {colonnes.map((col) => (
                                            <td key={col}>{row[col] ?? '—'}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {apercu.length > 50 && (
                            <div style={{ padding: '8px 16px', fontSize: 12, color: '#888' }}>
                                … et {apercu.length - 50} ligne(s) supplémentaire(s) non affichée(s).
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ImportTab;
